import * as Haptics from 'expo-haptics';
import { PencilIcon } from 'lucide-react-native';
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    FlatList,
    type LayoutChangeEvent,
    type NativeScrollEvent,
    type NativeSyntheticEvent,
    Platform,
} from 'react-native';
import { View, XStack, YStack } from 'tamagui';

import { H5Emphasized } from '../text/Typography';

const calculateCurrentValue = (
    scrollPosition: number,
    stepWidth: number,
    gapBetweenItems: number,
    min: number,
    max: number,
    step: number,
    fractionDigits: number,
): string => {
    const index = Math.round(scrollPosition / (stepWidth + gapBetweenItems));
    return Math.min(Math.max(index * step + min, min), max).toFixed(
        fractionDigits,
    );
};

interface SlideRulePickerTickProps {
    gapBetweenSteps?: number;
    shortStepHeight?: number;
    longStepHeight?: number;
    stepWidth?: number;
    shortStepColor?: string;
    longStepColor?: string;
}

const SlideRulePickerTick = React.memo(
    ({
        isLast,
        index,
        gapBetweenSteps,
        shortStepHeight,
        longStepHeight,
        stepWidth,
        shortStepColor,
        longStepColor,
    }: {
        index: number;
        isLast: boolean;
    } & SlideRulePickerTickProps) => {
        const isLong = index % 10 === 0;
        const height = isLong ? longStepHeight : shortStepHeight;

        return (
            <View
                width={stepWidth}
                height={height}
                borderRadius={4}
                justifyContent="center"
                marginRight={isLast ? 0 : gapBetweenSteps}
                backgroundColor={isLong ? longStepColor : shortStepColor}
                opacity={0.3}
            />
        );
    },
);

type SlideRulePickerProps = {
    min: number;
    max: number;
    step?: number;
    initialValue?: number;
    fractionDigits?: number;
    indicatorWidth?: number;
    indicatorHeight?: number;
    indicatorColor?: string;
    /** Vertical gap between the value label and the ruler. */
    verticalGap?: number;
    /**
     * When provided, renders a pencil affordance next to the value label and
     * calls this when tapped — e.g. to let the member type an exact amount.
     */
    onEditPress?: () => void;
    /**
     * Position the indicator exactly at `initialValue` even when it falls
     * between ticks (e.g. a manually-typed amount), keeping that exact value as
     * the label until the member drags — at which point it snaps to `step` like
     * normal. Defaults to false (initial value snaps to the nearest tick).
     */
    initialValueExact?: boolean;
    onValueChange?: (value: number) => void;
    onValueChangeEnd?: (value: number) => void;
    textFormatter?: (value: string) => string;
} & SlideRulePickerTickProps;

export const SlideRulePicker = ({
    min,
    max,
    step = 1,
    initialValue = min,
    fractionDigits = 0,
    indicatorWidth = 4,
    indicatorHeight = 88,
    gapBetweenSteps = 8,
    shortStepHeight = 38,
    longStepHeight = 48,
    stepWidth = 4,
    indicatorColor = '$black',
    shortStepColor = '$gray400',
    longStepColor = '$gray400',
    verticalGap = 48,
    onEditPress,
    initialValueExact = false,
    onValueChange,
    onValueChangeEnd,
    textFormatter,
}: SlideRulePickerProps) => {
    const itemAmount = (max - min) / step;
    const arrData = Array.from({ length: itemAmount + 1 }, (_, index) => index);

    const listRef = useRef<FlatList<typeof arrData>>(null);
    // Calculate initial display text based on initialValue
    const initialDisplayText = useMemo(() => {
        return textFormatter
            ? textFormatter(initialValue.toString())
            : initialValue.toFixed(fractionDigits).toString();
    }, [initialValue, textFormatter, fractionDigits]);
    const [displayText, setDisplayText] = useState(initialDisplayText);
    const [containerWidth, setContainerWidth] = useState(0);

    // Web drag state
    const webRef = useRef<HTMLDivElement | null>(null);
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const scrollStartX = useRef(0);
    // Track scroll position and time for velocity calculation
    const lastScrollX = useRef(0);
    const lastScrollTime = useRef(0);
    const velocity = useRef(0);
    const momentumAnimationFrame = useRef<number | null>(null);
    // Global pointerup handler to catch releases outside the component
    const handleGlobalPointerUpRef = useRef<((e: PointerEvent) => void) | null>(
        null,
    );

    // Track if we're doing initial scroll to prevent scroll handler from overriding
    const isInitializingScroll = useRef(false);
    // Track if FlatList has been laid out
    const hasLaidOut = useRef(false);
    // Track if FlatList content has been measured
    const hasContentSize = useRef(false);
    // Track if we're doing initial scroll on web to prevent scroll handler from overriding
    const isInitializingWebScroll = useRef(false);
    // Track if web container has been laid out
    const hasWebLaidOut = useRef(false);
    // Track previous step index to only trigger haptics when value changes
    const previousStepIndexRef = useRef<number | null>(null);
    // Track if we've manually stopped momentum to prevent conflicts
    const hasManuallyStoppedMomentum = useRef(false);
    // Track if we're currently in a momentum scroll
    const isInMomentumScroll = useRef(false);
    // Track last scroll position to detect momentum (native only)
    const lastNativeScrollPosition = useRef<number | null>(null);
    const lastNativeScrollTime = useRef<number>(0);
    // Track if we're currently in an active drag to ignore stale momentum events
    const isInActiveDrag = useRef(false);

    // Update label value
    const updateValue = useCallback(
        (scrollLeft: number) => {
            const newStep = calculateCurrentValue(
                scrollLeft,
                stepWidth,
                gapBetweenSteps,
                min,
                max,
                step,
                fractionDigits,
            );
            setDisplayText(textFormatter ? textFormatter(newStep) : newStep);
            onValueChange?.(Number(newStep));

            // Calculate the step index to determine if it's a long tick (every 10th step)
            const stepIndex = Math.round(
                scrollLeft / (stepWidth + gapBetweenSteps),
            );

            // Only trigger haptic feedback when the step index actually changes
            if (
                Platform.OS !== 'web' &&
                previousStepIndexRef.current !== stepIndex
            ) {
                const isLongTick = stepIndex % 10 === 0;
                const feedbackStyle = isLongTick
                    ? Haptics.ImpactFeedbackStyle.Medium
                    : Haptics.ImpactFeedbackStyle.Light;
                void Haptics.impactAsync(feedbackStyle);
                previousStepIndexRef.current = stepIndex;
            }

            return newStep;
        },
        [
            fractionDigits,
            gapBetweenSteps,
            stepWidth,
            max,
            min,
            onValueChange,
            step,
            textFormatter,
        ],
    );

    // Native scrolling
    const handleNativeScroll = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
            // Skip updating value during initial scroll to prevent incorrect recalculation
            if (isInitializingScroll.current) return;
            if (containerWidth === 0) return;
            const scrollLeft = e.nativeEvent.contentOffset.x;

            // Track momentum state by detecting if scroll is moving without user interaction
            const now = Date.now();
            if (lastNativeScrollPosition.current !== null) {
                const timeDelta = now - lastNativeScrollTime.current;
                const positionDelta = Math.abs(
                    scrollLeft - lastNativeScrollPosition.current,
                );
                // If scroll position is changing significantly without recent user interaction, we're in momentum
                if (timeDelta > 0 && positionDelta > 1 && timeDelta < 100) {
                    isInMomentumScroll.current = true;
                }
            }
            lastNativeScrollPosition.current = scrollLeft;
            lastNativeScrollTime.current = now;

            // The center of the visible area is at scrollLeft + containerWidth / 2 from content start
            // Items start after the separator, so subtract separator width to get item position
            const separatorWidth = containerWidth / 2 - stepWidth / 2;
            // Center position relative to content start
            const centerPosition = scrollLeft + containerWidth / 2;
            // Item offset is center position minus separator (where items start)
            const itemOffset = centerPosition - separatorWidth;
            // Clamp to valid range: allow negative (for index 0), but cap at max
            const maxItemOffset = itemAmount * (stepWidth + gapBetweenSteps);
            const clampedOffset = Math.min(itemOffset, maxItemOffset);
            // Don't clamp to 0 - let calculateCurrentValue handle negative values (they become index 0)
            updateValue(clampedOffset);
        },
        [updateValue, containerWidth, stepWidth, itemAmount, gapBetweenSteps],
    );

    // Native snapping - shared logic for both onScrollEndDrag and onMomentumScrollEnd
    const performSnap = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
            // Skip snapping during initial scroll to prevent resetting position
            if (isInitializingScroll.current) return;
            // Skip if container width not available yet
            if (containerWidth === 0) return;
            const scrollLeft = e.nativeEvent.contentOffset.x;
            // The center of the visible area is at scrollLeft + containerWidth / 2 from content start
            const separatorWidth = containerWidth / 2 - stepWidth / 2;
            // Center position relative to content start
            const centerPosition = scrollLeft + containerWidth / 2;
            // Item offset is center position minus separator (where items start)
            const itemOffset = centerPosition - separatorWidth;
            const tickWidth = stepWidth + gapBetweenSteps;
            // Calculate nearest tick - handle negative values correctly
            const nearestTick = Math.round(itemOffset / tickWidth);
            // Clamp to valid range (0 to itemAmount) - negative values become 0
            const clampedTick = Math.max(0, Math.min(nearestTick, itemAmount));
            // Calculate the center of the target tick (tick center is at stepWidth / 2 from tick start)
            const targetItemOffset = clampedTick * tickWidth;
            const targetTickCenter = targetItemOffset + stepWidth / 2;
            // To center the target tick center under the indicator center:
            // scrollLeft + containerWidth / 2 = separatorWidth + targetTickCenter
            // So: scrollLeft = separatorWidth + targetTickCenter - containerWidth / 2
            const targetScroll =
                separatorWidth + targetTickCenter - containerWidth / 2;

            // Always snap to ensure perfect centering, but only animate if offset is significant
            const currentOffset = Math.abs(itemOffset - targetItemOffset);
            // Always update the scroll position to ensure perfect centering
            // Use a small threshold to avoid unnecessary animations for tiny adjustments
            const shouldAnimate = currentOffset > 1; // 1px threshold for animation
            listRef.current?.scrollToOffset({
                offset: targetScroll,
                animated: shouldAnimate,
            });
            const snappedValue = updateValue(targetItemOffset);
            onValueChangeEnd?.(Number(snappedValue));
        },
        [
            stepWidth,
            gapBetweenSteps,
            containerWidth,
            itemAmount,
            updateValue,
            onValueChangeEnd,
        ],
    );

    // Handle scroll begin drag - stop any ongoing momentum scroll when user starts dragging
    const handleNativeScrollBeginDrag = useCallback(
        (_e: NativeSyntheticEvent<NativeScrollEvent>) => {
            // Mark that we're in an active drag
            isInActiveDrag.current = true;

            // Only mark that we stopped momentum if we're actually in a momentum scroll
            // Don't call scrollToOffset here - let the drag gesture naturally take over
            // Calling scrollToOffset conflicts with active drag gestures and causes bouncing
            // React Native's FlatList will automatically stop momentum when drag begins
            if (isInMomentumScroll.current) {
                hasManuallyStoppedMomentum.current = true;
                isInMomentumScroll.current = false;
            } else if (!hasManuallyStoppedMomentum.current) {
                // Normal drag start (not following an interruption) - clear momentum state
                isInMomentumScroll.current = false;
            }
            // If hasManuallyStoppedMomentum is true, keep it for this drag session
        },
        [],
    );

    // Handle scroll end when user stops dragging (for slow drags without momentum)
    const handleNativeScrollEndDrag = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
            // Check velocity first - if there's momentum, allow it to work normally
            const velocity = e.nativeEvent.velocity?.x ?? 0;

            // If we manually stopped momentum, check if this new drag has velocity
            if (hasManuallyStoppedMomentum.current) {
                hasManuallyStoppedMomentum.current = false;

                if (Math.abs(velocity) > 0.1) {
                    // This new drag has momentum, allow it to work normally
                    isInMomentumScroll.current = true;
                    // Clear isInActiveDrag so onMomentumScrollEnd can fire for this legitimate momentum
                    // The stale onMomentumScrollEnd from interrupted scroll should have already fired or been ignored
                    isInActiveDrag.current = false;
                } else {
                    // No momentum, snap immediately
                    isInMomentumScroll.current = false;
                    lastNativeScrollPosition.current = null;
                    // Clear isInActiveDrag after a short delay to allow any stale onMomentumScrollEnd to be ignored
                    setTimeout(() => {
                        isInActiveDrag.current = false;
                    }, 100);
                    performSnap(e);
                }
                return;
            }

            // Normal drag end - check velocity
            if (Math.abs(velocity) > 0.1) {
                // We have momentum, so we'll be in momentum scroll
                isInMomentumScroll.current = true;
            } else {
                // No momentum, snap immediately
                isInMomentumScroll.current = false;
                lastNativeScrollPosition.current = null;
                isInActiveDrag.current = false;
                performSnap(e);
            }
        },
        [performSnap],
    );

    // Handle scroll end when momentum stops (for fast drags with momentum)
    const handleNativeScrollEnd = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
            // Ignore if we manually stopped momentum and we're not in a momentum scroll (stale event)
            // This handles the case where onMomentumScrollEnd fires from the old interrupted scroll
            if (
                hasManuallyStoppedMomentum.current &&
                !isInMomentumScroll.current
            ) {
                // This is a stale momentum event from interrupted scroll
                hasManuallyStoppedMomentum.current = false;
                isInActiveDrag.current = false;
                return;
            }

            // If isInMomentumScroll is false, this might be a stale event, but if we're not in active drag
            // and hasManuallyStoppedMomentum is false, it's legitimate
            if (!isInMomentumScroll.current && isInActiveDrag.current) {
                // Stale event, ignore it
                return;
            }

            // Clear momentum state
            isInMomentumScroll.current = false;
            lastNativeScrollPosition.current = null;
            isInActiveDrag.current = false;

            // Clear the flag if it was set (shouldn't be at this point, but just in case)
            if (hasManuallyStoppedMomentum.current) {
                hasManuallyStoppedMomentum.current = false;
            }

            performSnap(e);
        },
        [performSnap],
    );

    // Web pointer events
    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        // Clear any pending snap timeout when starting to drag
        if (scrollEndTimeout.current) {
            clearTimeout(scrollEndTimeout.current);
            scrollEndTimeout.current = null;
        }
        // Cancel any momentum animation
        if (momentumAnimationFrame.current !== null) {
            cancelAnimationFrame(momentumAnimationFrame.current);
            momentumAnimationFrame.current = null;
        }
        isDragging.current = true;
        dragStartX.current = e.clientX;
        scrollStartX.current = webRef.current?.scrollLeft ?? 0;
        // Reset velocity tracking
        lastScrollX.current = scrollStartX.current;
        lastScrollTime.current = Date.now();
        velocity.current = 0;

        // Add global pointerup listener to catch releases outside the component
        if (Platform.OS === 'web' && handleGlobalPointerUpRef.current) {
            document.addEventListener(
                'pointerup',
                handleGlobalPointerUpRef.current,
                true,
            );
            document.addEventListener(
                'pointercancel',
                handleGlobalPointerUpRef.current,
                true,
            );
        }
    };
    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDragging.current || !webRef.current) return;
        const deltaX = e.clientX - dragStartX.current;
        const currentScrollX = scrollStartX.current - deltaX;
        webRef.current.scrollLeft = currentScrollX;
        updateValue(currentScrollX);

        // Calculate velocity for momentum
        const now = Date.now();
        if (lastScrollTime.current > 0) {
            const timeDelta = now - lastScrollTime.current;
            const scrollDelta = currentScrollX - lastScrollX.current;
            if (timeDelta > 0) {
                velocity.current = scrollDelta / timeDelta; // pixels per millisecond
            }
        }
        lastScrollX.current = currentScrollX;
        lastScrollTime.current = now;
    };
    const handlePointerUpOrLeave = useCallback(() => {
        if (!webRef.current) return;
        const wasDragging = isDragging.current;
        isDragging.current = false;

        // Remove global listener when drag ends
        if (Platform.OS === 'web' && handleGlobalPointerUpRef.current) {
            document.removeEventListener(
                'pointerup',
                handleGlobalPointerUpRef.current,
                true,
            );
            document.removeEventListener(
                'pointercancel',
                handleGlobalPointerUpRef.current,
                true,
            );
            handleGlobalPointerUpRef.current = null;
        }

        // If we were dragging and have velocity, apply momentum
        if (wasDragging && Math.abs(velocity.current) > 0.1) {
            // Cancel any existing momentum animation
            if (momentumAnimationFrame.current !== null) {
                cancelAnimationFrame(momentumAnimationFrame.current);
            }

            const startScrollX = webRef.current.scrollLeft;
            const startVelocity = velocity.current;
            const friction = 0.95; // Deceleration factor
            let currentVelocity = startVelocity;
            let currentScrollX = startScrollX;

            const animateMomentum = () => {
                if (!webRef.current) {
                    momentumAnimationFrame.current = null;
                    return;
                }

                // Apply deceleration
                currentVelocity *= friction;
                currentScrollX += currentVelocity * 16; // Assume ~60fps (16ms per frame)

                // Stop if velocity is too low
                if (Math.abs(currentVelocity) < 0.1) {
                    // Snap to nearest tick
                    const tickWidth = stepWidth + gapBetweenSteps;
                    const nearestTick = Math.round(currentScrollX / tickWidth);
                    const targetScroll = nearestTick * tickWidth;

                    webRef.current.scrollTo({
                        left: targetScroll,
                        behavior: 'smooth',
                    });
                    const snappedValue = updateValue(targetScroll);
                    onValueChangeEnd?.(Number(snappedValue));
                    momentumAnimationFrame.current = null;
                    return;
                }

                // Update scroll position
                webRef.current.scrollLeft = currentScrollX;
                updateValue(currentScrollX);
                momentumAnimationFrame.current =
                    requestAnimationFrame(animateMomentum);
            };

            momentumAnimationFrame.current =
                requestAnimationFrame(animateMomentum);
        } else if (wasDragging) {
            // No momentum, just snap immediately. Only do this if a drag
            // actually happened — this handler is also bound to onPointerLeave,
            // and a passive hover-then-leave (no drag) must not snap the value
            // to the nearest tick (which would discard an exact typed amount).
            const tickWidth = stepWidth + gapBetweenSteps;
            const nearestTick = Math.round(
                webRef.current.scrollLeft / tickWidth,
            );
            const targetScroll = nearestTick * tickWidth;

            webRef.current.scrollTo({ left: targetScroll, behavior: 'smooth' });
            const snappedValue = updateValue(targetScroll);
            onValueChangeEnd?.(Number(snappedValue));
        }

        // Reset velocity tracking
        lastScrollTime.current = 0;
        velocity.current = 0;
    }, [stepWidth, gapBetweenSteps, updateValue, onValueChangeEnd]);

    // Set up global pointerup handler for web
    useEffect(() => {
        if (Platform.OS !== 'web') return;

        handleGlobalPointerUpRef.current = () => {
            if (isDragging.current) {
                // Handle pointer up as if it happened on the component
                handlePointerUpOrLeave();
            }
        };

        return () => {
            if (handleGlobalPointerUpRef.current) {
                document.removeEventListener(
                    'pointerup',
                    handleGlobalPointerUpRef.current,
                    true,
                );
                document.removeEventListener(
                    'pointercancel',
                    handleGlobalPointerUpRef.current,
                    true,
                );
            }
        };
    }, [handlePointerUpOrLeave]);

    const scrollEndTimeout = useRef<NodeJS.Timeout | null>(null);
    const handleWebScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!webRef.current) return;
        // Skip updating value during initial scroll to prevent incorrect recalculation
        if (isInitializingWebScroll.current) return;

        const scrollLeft = e.currentTarget.scrollLeft;

        if (!isDragging.current) updateValue(scrollLeft);

        // Only set up snapping timeout when NOT dragging
        // Snapping during drags is handled by handlePointerUpOrLeave
        if (!isDragging.current) {
            if (scrollEndTimeout.current)
                clearTimeout(scrollEndTimeout.current);
            scrollEndTimeout.current = setTimeout(() => {
                if (!webRef.current) return;
                // Skip snapping during initial scroll or if dragging started
                if (isInitializingWebScroll.current || isDragging.current)
                    return;

                const tickWidth = stepWidth + gapBetweenSteps;
                const nearestTick = Math.round(
                    webRef.current.scrollLeft / tickWidth,
                );
                const targetScroll = nearestTick * tickWidth;

                webRef.current.scrollTo({
                    left: targetScroll,
                    behavior: 'smooth',
                });
                const snappedValue = updateValue(targetScroll);
                onValueChangeEnd?.(Number(snappedValue));
            }, 100);
        } else {
            // Clear any pending snap timeout when dragging
            if (scrollEndTimeout.current) {
                clearTimeout(scrollEndTimeout.current);
                scrollEndTimeout.current = null;
            }
        }
    };

    const onLayout = (event: LayoutChangeEvent) => {
        setContainerWidth(event.nativeEvent.layout.width);
    };

    const scrollToInitial = () => {
        const initialIndex = Math.floor((initialValue - min) / step);
        const itemOffset = initialIndex * (stepWidth + gapBetweenSteps);
        // Fractional offset lands the indicator between ticks at the exact
        // value when `initialValueExact` is set (see prop docs).
        const fractionalIndex = Math.max(
            0,
            Math.min((initialValue - min) / step, itemAmount),
        );
        const exactOffset = fractionalIndex * (stepWidth + gapBetweenSteps);
        if (Platform.OS === 'web') {
            // Set flag to prevent scroll handler from interfering during initial scroll
            isInitializingWebScroll.current = true;
            const targetOffset = initialValueExact ? exactOffset : itemOffset;
            // Use requestAnimationFrame to ensure container is ready
            const attemptScroll = () => {
                if (webRef.current) {
                    webRef.current.scrollTo({
                        left: targetOffset,
                        behavior: 'auto',
                    });
                    // In exact mode the label already shows the typed value; a
                    // recalc here would round it to the nearest tick.
                    if (!initialValueExact) updateValue(itemOffset);
                    // Verify scroll position after a brief delay and retry if needed
                    setTimeout(() => {
                        if (
                            webRef.current &&
                            Math.abs(webRef.current.scrollLeft - targetOffset) >
                                1
                        ) {
                            // Retry if scroll didn't complete
                            webRef.current.scrollTo({
                                left: targetOffset,
                                behavior: 'auto',
                            });
                        }
                        // Clear flag after scroll events settle
                        setTimeout(() => {
                            isInitializingWebScroll.current = false;
                        }, 50);
                    }, 50);
                } else {
                    // Retry if ref not ready yet
                    requestAnimationFrame(attemptScroll);
                }
            };
            requestAnimationFrame(() => {
                requestAnimationFrame(attemptScroll);
            });
        } else {
            // Set flag to prevent scroll handler from interfering during initial scroll
            isInitializingScroll.current = true;
            // Clamp initialIndex to valid range
            const clampedInitialIndex = Math.max(
                0,
                Math.min(initialIndex, itemAmount),
            );

            // Retry function to attempt scrolling
            const attemptScroll = (retryCount = 0) => {
                if (!listRef.current) {
                    // If ref not ready, retry after a short delay
                    if (retryCount < 10) {
                        setTimeout(() => {
                            attemptScroll(retryCount + 1);
                        }, 50);
                    } else {
                        isInitializingScroll.current = false;
                    }
                    return;
                }

                // Use double requestAnimationFrame to ensure FlatList layout is complete
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (listRef.current) {
                            // Use scrollToIndex with viewOffset to center the item
                            // getItemLayout includes separator, so scrollToIndex positions item correctly
                            // When scrollToIndex scrolls to an item, it positions the item's left edge
                            // at the viewport edge (0), then applies viewOffset
                            // The tick center is at stepWidth / 2 from the item start
                            // We want tick center at containerWidth / 2 in the viewport
                            // So: stepWidth / 2 + viewOffset = containerWidth / 2
                            // Therefore: viewOffset = containerWidth / 2 - stepWidth / 2
                            const viewOffset =
                                containerWidth / 2 - stepWidth / 2;

                            if (initialValueExact) {
                                listRef.current.scrollToOffset({
                                    offset: exactOffset,
                                    animated: false,
                                });
                            } else {
                                listRef.current.scrollToIndex({
                                    index: clampedInitialIndex,
                                    viewOffset,
                                    animated: false,
                                });
                                // Update value with the item offset (not including separator)
                                updateValue(itemOffset);
                            }

                            // Verify scroll position after a brief delay and retry if needed
                            setTimeout(() => {
                                if (listRef.current) {
                                    // Retry scroll to ensure it completed
                                    if (initialValueExact) {
                                        listRef.current.scrollToOffset({
                                            offset: exactOffset,
                                            animated: false,
                                        });
                                    } else {
                                        listRef.current.scrollToIndex({
                                            index: clampedInitialIndex,
                                            viewOffset,
                                            animated: false,
                                        });
                                    }
                                }
                                // Clear flag after scroll events settle
                                setTimeout(() => {
                                    isInitializingScroll.current = false;
                                }, 50);
                            }, 100);
                        } else {
                            // If ref lost, retry
                            if (retryCount < 5) {
                                setTimeout(() => {
                                    attemptScroll(retryCount + 1);
                                }, 50);
                            } else {
                                isInitializingScroll.current = false;
                            }
                        }
                    });
                });
            };

            // Only attempt scroll if content size is available
            if (hasContentSize.current) {
                attemptScroll();
            } else {
                // Wait for content size, then retry
                const checkContentSize = setInterval(() => {
                    if (hasContentSize.current) {
                        clearInterval(checkContentSize);
                        attemptScroll();
                    }
                }, 50);
                // Timeout after 1 second
                setTimeout(() => {
                    clearInterval(checkContentSize);
                    if (!hasContentSize.current) {
                        // Try anyway if content size never becomes available
                        attemptScroll();
                    }
                }, 1000);
            }
        }
    };

    // Track if we've performed the initial scroll
    const hasPerformedInitialScroll = useRef(false);

    // Scroll function for perfect centering
    const performInitialScroll = useCallback(() => {
        if (
            Platform.OS !== 'web' &&
            containerWidth > 0 &&
            hasContentSize.current &&
            listRef.current &&
            !hasPerformedInitialScroll.current
        ) {
            const initialIndex = Math.floor((initialValue - min) / step);
            const clampedInitialIndex = Math.max(
                0,
                Math.min(initialIndex, itemAmount),
            );

            isInitializingScroll.current = true;
            hasPerformedInitialScroll.current = true;

            if (initialValueExact) {
                // Rest between ticks at the exact value (see prop docs). The
                // centering offset for a (possibly fractional) index i is
                // simply i * (tick width), so a fractional index lands the
                // indicator proportionally between two ticks.
                const fractionalIndex = Math.max(
                    0,
                    Math.min((initialValue - min) / step, itemAmount),
                );
                listRef.current.scrollToOffset({
                    offset: fractionalIndex * (stepWidth + gapBetweenSteps),
                    animated: false,
                });
            } else {
                // Use scrollToIndex with viewOffset for proper centering
                // The tick center is at stepWidth / 2 from the item start
                // We want tick center at containerWidth / 2 in the viewport
                // So: stepWidth / 2 + viewOffset = containerWidth / 2
                // Therefore: viewOffset = containerWidth / 2 - stepWidth / 2
                const viewOffset = containerWidth / 2 - stepWidth / 2;
                listRef.current.scrollToIndex({
                    index: clampedInitialIndex,
                    viewOffset,
                    animated: false,
                });
            }

            // Clear flag after a brief moment
            requestAnimationFrame(() => {
                isInitializingScroll.current = false;
            });
        }
    }, [
        containerWidth,
        initialValue,
        min,
        step,
        stepWidth,
        gapBetweenSteps,
        itemAmount,
        initialValueExact,
    ]);

    // Use useLayoutEffect to scroll synchronously before paint when ready
    useLayoutEffect(() => {
        performInitialScroll();
    }, [performInitialScroll]);

    useEffect(() => {
        if (containerWidth > 0) {
            if (Platform.OS === 'web') {
                // For web, wait for container to be ready
                if (hasWebLaidOut.current || webRef.current) {
                    scrollToInitial();
                }
            }
            // Native scroll is handled in useLayoutEffect for immediate scrolling
        }
    }, [
        containerWidth,
        initialValue,
        min,
        step,
        stepWidth,
        gapBetweenSteps,
        max,
        fractionDigits,
        textFormatter,
    ]);

    const renderItem = useCallback(
        ({ index }: { index: number }) => (
            <SlideRulePickerTick
                index={index}
                isLast={index === arrData.length - 1}
                shortStepHeight={shortStepHeight}
                longStepHeight={longStepHeight}
                gapBetweenSteps={gapBetweenSteps}
                stepWidth={stepWidth}
                shortStepColor={shortStepColor}
                longStepColor={longStepColor}
            />
        ),
        [
            arrData.length,
            gapBetweenSteps,
            stepWidth,
            longStepColor,
            longStepHeight,
            shortStepColor,
            shortStepHeight,
        ],
    );

    const renderSeparator = useCallback(
        () => <View width={containerWidth / 2 - stepWidth / 2} />,
        [containerWidth, stepWidth],
    );

    return (
        <YStack width="100%" gap={verticalGap} onLayout={onLayout}>
            <XStack alignItems="center" justifyContent="center" gap={8}>
                <H5Emphasized>{displayText}</H5Emphasized>
                {onEditPress != null && (
                    <View
                        onPress={onEditPress}
                        hitSlop={12}
                        cursor="pointer"
                        pressStyle={{ opacity: 0.5 }}
                        role="button"
                        aria-label="Enter an exact amount"
                    >
                        <PencilIcon size={20} />
                    </View>
                )}
            </XStack>

            <View
                justifyContent="center"
                alignItems="center"
                height={indicatorHeight}
                overflow="hidden"
            >
                {containerWidth > 0 &&
                    (Platform.OS === 'web' ? (
                        <div
                            ref={webRef}
                            data-sliderule-picker
                            style={{
                                width: '100%',
                                display: 'flex',
                                overflowX: 'auto',
                                scrollSnapType: 'x mandatory',
                                WebkitOverflowScrolling: 'touch',
                                scrollbarWidth: 'none',
                                cursor: 'ew-resize',
                                alignItems: 'center',
                            }}
                            onScroll={handleWebScroll}
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUpOrLeave}
                            onPointerLeave={handlePointerUpOrLeave}
                        >
                            <div
                                style={{
                                    width: containerWidth / 2 - stepWidth / 2,
                                    flexShrink: 0,
                                }}
                            />
                            {arrData.map((_, i) => (
                                <SlideRulePickerTick
                                    key={i}
                                    index={i}
                                    isLast={i === arrData.length - 1}
                                    shortStepHeight={shortStepHeight}
                                    longStepHeight={longStepHeight}
                                    gapBetweenSteps={gapBetweenSteps}
                                    stepWidth={stepWidth}
                                    shortStepColor={shortStepColor}
                                    longStepColor={longStepColor}
                                />
                            ))}
                            <div
                                style={{
                                    width: containerWidth / 2 - stepWidth / 2,
                                    flexShrink: 0,
                                }}
                            />
                        </div>
                    ) : (
                        <FlatList
                            ref={listRef}
                            data={arrData as unknown as ArrayLike<number[]>}
                            keyExtractor={(_, index) => index.toString()}
                            renderItem={renderItem}
                            ListHeaderComponent={renderSeparator}
                            ListFooterComponent={renderSeparator}
                            onScroll={handleNativeScroll}
                            onScrollBeginDrag={handleNativeScrollBeginDrag}
                            onScrollEndDrag={handleNativeScrollEndDrag}
                            onMomentumScrollEnd={handleNativeScrollEnd}
                            onLayout={() => {
                                if (!hasLaidOut.current) {
                                    hasLaidOut.current = true;
                                }
                                // Try to scroll immediately when layout is ready (synchronously)
                                performInitialScroll();
                            }}
                            onContentSizeChange={() => {
                                if (!hasContentSize.current) {
                                    hasContentSize.current = true;
                                    // Scroll immediately when content size is ready (synchronously)
                                    performInitialScroll();
                                }
                            }}
                            getItemLayout={(_, index) => {
                                const separatorWidth =
                                    containerWidth > 0
                                        ? containerWidth / 2 - stepWidth / 2
                                        : 0;
                                return {
                                    length: stepWidth + gapBetweenSteps,
                                    offset:
                                        separatorWidth +
                                        (stepWidth + gapBetweenSteps) * index,
                                    index,
                                };
                            }}
                            initialNumToRender={itemAmount + 1}
                            maxToRenderPerBatch={itemAmount + 1}
                            windowSize={21}
                            removeClippedSubviews={false}
                            updateCellsBatchingPeriod={1}
                            horizontal
                            scrollEventThrottle={16}
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={{
                                alignItems: 'center',
                            }}
                            // Prevent vertical scrolling from interfering with horizontal scrolling
                            nestedScrollEnabled={false}
                            // Disable vertical bounce to prevent gesture conflicts
                            alwaysBounceVertical={false}
                            alwaysBounceHorizontal={false}
                        />
                    ))}
                <View
                    pointerEvents="none"
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform={[
                        { translateX: -indicatorWidth / 2 },
                        { translateY: -indicatorHeight / 2 },
                    ]}
                    alignItems="center"
                >
                    <View
                        width={indicatorWidth}
                        height={indicatorHeight}
                        borderRadius={4}
                        backgroundColor={indicatorColor}
                    />
                </View>
            </View>
        </YStack>
    );
};
